/* eslint-disable no-useless-escape */
/* eslint-disable no-shadow */
import React, {
  useState,
} from 'react';
import { connect } from 'react-redux';
import { businessActions, notificationActions } from 'store/actions';
import Loading from 'components/Loading';
import { SpButton, SpStatusTag, SpText } from 'components/DesignKit';
import {
  Card, Icon, Row, Select, Table,
  Tooltip,
} from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import getCustomDocumentBackground from 'utils/getCustomDocumentBackground';
import getCustomDocStatusColor from 'utils/getCustomDocStatusColor';
import getCustomDocumentStatus from 'utils/getCustomDocumentStatus';
import Download from 'components/NavIcons/downloadProof';
import { NOTIFICATION } from 'store/actionTypes';
import { MESSAGES } from 'appconstants';
import { API_END_POINTS, axiosAuthInstance } from 'api';
import DocumentConfirmationModal from './DocumentConfirmationModal';

const { Option } = Select;

type Props = {
  paypalRequiredDocuments: Object,
  loading: Boolean,
  PaypalDocumentUpload: Function,
  PaypalDocEmailRequest: Function,
  documents: Array<Object>,
  setNotification: Function,
};

const RequiredDocumentUpload = (props: Props) => {
  const {
    paypalRequiredDocuments,
    loading,
    PaypalDocumentUpload,
    PaypalDocEmailRequest,
    documents,
    setNotification,
  } = props;

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const onDocumentUpload = (documentUrl: string) => {
    PaypalDocumentUpload({
      documentName: selectedDocument?.friendlyName,
      requiredDocumentUrl: documentUrl,
      paypalDocumentReferenceId: selectedDocument?.documentId,
    });
    setOpenModal(false);
  };

  const onDownloadDocument = async (record) => {
    const documentGid = record.gid;
    const downloadPath = record.responseDocumentUrl ? 'response/download' : 'request/download';
    try {
      const { data: { entity: { fileUrl } } } = await axiosAuthInstance
        .get(`${API_END_POINTS.CUSTOM_DOCUMENT_REQUEST}/${documentGid}/${downloadPath}`);
      window.open(fileUrl);
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: MESSAGES.CUSTOM_DOCUMENT.DOWNLOAD_ERROR,
      });
    }
  };

  const columns = [
    {
      title: 'SELECT DOCUMENT TO UPLOAD',
      width: 300,
      align: 'start',
      sorter: false,
      render: (record, _, index) => (
        <Select
          style={{ width: 300 }}
          bordered
          onChange={(value) => {
            const selected = record?.group.items.find(doc => doc.documentId === value);
            setSelectedDocument({ ...selected, rowIndex: index });
          }}
          value={selectedDocument?.rowIndex === index ? selectedDocument?.documentId : ''}
        >
          {record?.group?.items?.map((item) => (
            <Option key={item?.documentId} value={item?.documentId}>
              {item?.friendlyName}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: '',
      width: 300,
      align: 'end',
      sorter: false,
      render: (_, __, index) => (
        <Tooltip placement="bottom" title={<span>Upload</span>}>
          <CloudUploadOutlined
            style={{ fontSize: '30px', color: '#3E8BFF', display: selectedDocument?.rowIndex === index ? '' : 'none' }}
            onClick={() => setOpenModal(true)}
          />
        </Tooltip>
      ),
    },
  ];

  const documentColumns = [
    {
      title: 'TYPE',
      dataIndex: 'type',
      width: '25%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      width: '25%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      width: '25%',
      align: 'left',
      sorter: false,
      render: (status) => (
        <Tooltip placement="top" title="Click here to view details">
          <SpStatusTag style={{ backgroundColor: getCustomDocumentBackground(status), color: getCustomDocStatusColor(status) }}>
            {getCustomDocumentStatus(status)}
          </SpStatusTag>
        </Tooltip>
      ),
    },
  ];

  const customActionColumn = {
    title: 'ACTION',
    dataIndex: 'action',
    width: '20%',
    align: 'center',
    render: (text, record) => (
      <span>
        <Icon
          component={Download}
          style={{ fontSize: '24px', marginRight: '8px', cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onDownloadDocument(record); }}
        />
      </span>
    ),
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {paypalRequiredDocuments && paypalRequiredDocuments?.items?.length > 0 ? (
        <Card>
          <Row className="my-2">
            <div className="d-flex justify-content-end">
              <SpButton
                type="secondary"
                shape="round"
                ghost
                className="mr-3"
                onClick={() => PaypalDocEmailRequest()}
              >
                {'\u002B'}&nbsp;Create Document Request
              </SpButton>
            </div>
          </Row>
          <Table
            rowClassName={() => 'ant-table-clickable-row'}
            columns={columns.map(column => column)}
            rowKey={(record, index) => index}
            dataSource={paypalRequiredDocuments?.items}
            pagination={false}
          />
          {openModal && (
            <DocumentConfirmationModal
              visible={openModal}
              selectedDocument={selectedDocument}
              close={() => setOpenModal(false)}
              submit={onDocumentUpload}
            />
          )}
        </Card>
      ) : null }
      {documents && documents?.length > 0 ? (
        <Card className="mt-3">
          <SpText className="mb-4 mt-4" fontSize="17px" color="#999999" fontWeight="700">
            UPLOADED DOCUMENTS
          </SpText>
          <Table
            rowClassName={() => 'ant-table-clickable-row'}
            columns={[...documentColumns, customActionColumn]}
            rowKey={(record) => record?.gid}
            dataSource={documents}
            pagination={false}
          />
        </Card>
      ) : null }
    </>
  );
};

const mapStateToProps = state => ({
  selectedAccount: state.account.selectedAccount,
  test: state.account.test,
  isSuperUser: state.user.superUser,
  loading: state.loading.loading,
  validator: state.loading.validator,
  paypalRequiredDocuments: state.business.paypalRequiredDocuments,
  documents: state.customDocumentRequest.content,
  submitting: state.loading.submitting,
});

const mapDispatchToProps = (dispatch) => ({
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
  fetchPaypalVerification: gid => dispatch(businessActions.fetchPaypalVerification({
    payload: gid,
  })),
  PaypalDocumentUpload: param => dispatch(businessActions.PaypalDocumentUpload({
    payload: param,
  })),
  PaypalDocEmailRequest: () => dispatch(businessActions.PaypalDocEmailRequest()),
});

// $FlowFixMe
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RequiredDocumentUpload);
